import { API_HOST, API_HOST_LAMBDA } from '@disco/accounts/utils/config'

const ENDPOINTS = {
  SIGNUP: '/api/signup/',
  BUSINESS_LOOKUP: '/api/business_lookup/',
  COOKIE_CONSENT: '/api/cookie_consent/',
  FIND_DISCO: '/api/find-disco/',
  APP_STATE: '/api/app_state/',
}

async function checkStatus(response: Response): Promise<Response> {
  if (response.status >= 200 && response.status < 300) return response

  const error = new Error(response.statusText)
  ;(error as Error & { status: number; response: unknown }).status =
    response.status
  ;(error as Error & { status: number; response: unknown }).response =
    await response.json()
  throw error
}

async function parseJSON<ResponseData>(
  response: Response,
): Promise<ResponseData> {
  // @ts-expect-error null response is not expected
  if (response.status === 204) return null
  return response.json()
}

async function apiCall<ResponseData = unknown>({
  method,
  endpoint,
  data,
  headers: customHeaders = {},
}: {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  endpoint: string
  data?: unknown
  headers?: Record<string, string>
}): Promise<ResponseData> {
  const body = data ? JSON.stringify(data) : undefined

  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...customHeaders,
  }

  const externalCall = endpoint.startsWith('http')
  const path = externalCall ? endpoint : `${API_HOST}${endpoint}`

  return fetch(path, {
    method,
    headers: externalCall ? {} : headers,
    credentials: !externalCall ? 'include' : undefined,
    body,
  })
    .then(checkStatus)
    .then(parseJSON) as Promise<ResponseData>
}

export async function getAppState(): Promise<unknown> {
  try {
    return await apiCall({
      method: 'GET',
      endpoint: ENDPOINTS.APP_STATE,
    })
  } catch (error) {
    console.error('App state check failed:', error)
    return null
  }
}

export async function generateLogo(businessName: string): Promise<Blob> {
  const logogenUrl = `${API_HOST_LAMBDA}/cat/api/logogen?${new URLSearchParams({
    t: businessName,
  })}`

  const response = await fetch(logogenUrl)
  if (!response.ok) {
    throw new Error(`Logo generation failed: ${response.statusText}`)
  }

  return response.blob()
}

type SignupResponse = {
  id: number
}

export async function signup(
  data: Record<string, unknown>,
): Promise<SignupResponse> {
  if (data.logo) {
    const formData = new FormData()
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key] as string | Blob)
      }
    })

    const response = await fetch(`${API_HOST}${ENDPOINTS.SIGNUP}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })

    if (!response.ok) {
      const error = new Error(response.statusText)
      ;(error as Error & { status: number; response: unknown }).status =
        response.status
      ;(error as Error & { status: number; response: unknown }).response =
        await response.json()
      throw error
    }

    return response.json()
  }

  return apiCall({
    method: 'POST',
    endpoint: ENDPOINTS.SIGNUP,
    data,
  })
}

export { API_HOST }
